import i18n from 'i18n';

import { FieldDevicePhotoResolutionKey, PublicUtilityType } from 'models';

/** Android WorkManager raises a shorter periodic interval to 15 minutes. */
export const MIN_DATA_SYNC_INTERVAL_MINUTES = 15;

export const MAX_DATA_SYNC_INTERVAL_MINUTES = 1440;

/** The backend rejects anything above ten years on every retention field. */
export const MAX_RETENTION_DAYS = 3650;

/** The device deletes health and safety reports before it uploads them. */
export const MIN_HEALTH_AND_SAFETY_RETENTION_DAYS = 7;

export const MAX_PHOTO_COUNT = 10;

export const MAX_APPOINTMENT_MINUTES = 1440;

export type SettingFieldType = 'number' | 'checkbox' | 'resolution' | 'range';

export interface SettingRowConfig {
  /**
   * Formik field path. Relative to the utility branch (`utilityTypes.<PublicUtilityType>`),
   * or an absolute path when the row is global.
   */
  name: string;
  label: string;
  description?: string;
  type: SettingFieldType;
  /** The setting is shared by both utility types, so it is rendered once. */
  isGlobal?: boolean;
  /** The setting does not apply to these utility types, so their cell stays empty. */
  hiddenFor?: PublicUtilityType[];
  maxLength?: number;
}

export interface SettingSectionConfig {
  key: string;
  title: string;
  rows: SettingRowConfig[];
}

const TRANSLATION_PREFIX = 'KOZOS_TEREPI_ESZKOZ_BEALLITASOK';

const key = (suffix: string) => `${TRANSLATION_PREFIX}.${suffix}`;

export interface UtilityColumnConfig {
  utilityType: PublicUtilityType;
  label: string;
}

export const UTILITY_COLUMNS: UtilityColumnConfig[] = [
  { utilityType: PublicUtilityType.ELECTRICITY, label: key('COLUMN_ELECTRICITY') },
  { utilityType: PublicUtilityType.GAS, label: key('COLUMN_GAS') },
];

export const SETTING_SECTIONS: SettingSectionConfig[] = [
  {
    key: 'retention',
    title: key('SECTION_RETENTION'),
    rows: [
      {
        name: 'task.deleteAfterReadingDays',
        label: key('TASK_DELETION_AFTER_READING'),
        type: 'number',
        maxLength: 4,
      },
      {
        name: 'task.deleteAfterClosingDays',
        label: key('TASK_DELETION_AFTER_CLOSING'),
        type: 'number',
        maxLength: 4,
      },
      {
        name: 'photo.deleteAfterDays',
        label: key('PHOTO_DELETION'),
        type: 'number',
        maxLength: 4,
      },
      {
        name: 'message.deleteAfterDays',
        label: key('MESSAGE_DELETION'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
      {
        name: 'read.keepWithdrawnTaskInDays',
        label: key('WITHDRAWN_READING_TASK_DELETION'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
      {
        name: 'check.keepWithdrawnTaskInDays',
        label: key('WITHDRAWN_CHECK_TASK_DELETION'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
      {
        name: 'check.keepCompletedTaskInDays',
        label: key('CHECK_TASK_DELETION_AFTER_APPOINTMENT'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
      {
        name: 'healthAndSafety.keepHealthAndSafetiesInDays',
        label: key('HEALTH_AND_SAFETY_DELETION'),
        description: key('HEALTH_AND_SAFETY_DELETION_DESCRIPTION'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
      {
        name: 'synchronization.periodicSynchronizationRepeatInMinutes',
        label: key('DATA_SYNC_INTERVAL'),
        description: key('DATA_SYNC_INTERVAL_DESCRIPTION'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
    ],
  },
  {
    key: 'gps',
    title: key('SECTION_GPS'),
    rows: [
      {
        name: 'gps.required',
        label: key('GPS_REQUIRED'),
        type: 'checkbox',
      },
      {
        name: 'gps.coordinateValidityInMinutes',
        label: key('GPS_VALIDITY'),
        type: 'number',
        maxLength: 4,
      },
      {
        name: 'gpsTracking.enabled',
        label: key('GPS_TRACKING'),
        type: 'checkbox',
        isGlobal: true,
      },
    ],
  },
  {
    key: 'device',
    title: key('SECTION_DEVICE'),
    rows: [
      {
        name: 'readingConfirmField.enabled',
        label: key('READING_CONFIRMATION'),
        type: 'checkbox',
      },
      {
        name: 'maintenanceMenu.enabled',
        label: key('MAINTENANCE_MENU'),
        type: 'checkbox',
        isGlobal: true,
      },
    ],
  },
  {
    key: 'photo',
    title: key('SECTION_PHOTO'),
    rows: [
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.METER}`,
        label: key('PHOTO_SCALE'),
        type: 'resolution',
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.DEVICE}`,
        label: key('PHOTO_METER_SITE'),
        type: 'resolution',
        hiddenFor: [PublicUtilityType.GAS],
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.REPORT}`,
        label: key('PHOTO_REPORT'),
        type: 'resolution',
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.NOT_READABLE_CONSUMPTION_PLACE}`,
        label: key('PHOTO_READING_FAILURE_CONSUMPTION_PLACE'),
        type: 'resolution',
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.NOT_READABLE_DEVICE}`,
        label: key('PHOTO_READING_FAILURE_METER'),
        type: 'resolution',
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.NOT_READABLE_METER}`,
        label: key('PHOTO_READING_FAILURE_SCALE'),
        type: 'resolution',
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.DOCUMENT}`,
        label: key('PHOTO_DOCUMENT'),
        type: 'resolution',
        hiddenFor: [PublicUtilityType.GAS],
      },
      {
        name: `photo.resolution.${FieldDevicePhotoResolutionKey.HEALTH_AND_SAFETY}`,
        label: key('PHOTO_HEALTH_AND_SAFETY'),
        description: key('PHOTO_HEALTH_AND_SAFETY_DESCRIPTION'),
        type: 'resolution',
      },
      {
        name: 'photo.minQualityScore',
        label: key('PHOTO_QUALITY_MINIMUM_SCORE'),
        type: 'number',
        maxLength: 3,
      },
      {
        name: 'photo.retryCount',
        label: key('PHOTO_QUALITY_RETRY_COUNT'),
        type: 'number',
        maxLength: 2,
      },
    ],
  },
  {
    key: 'photoCount',
    title: key('SECTION_PHOTO_COUNT'),
    rows: [
      {
        name: 'read.meterPhotoCount',
        label: key('READ_SCALE_PHOTO_COUNT'),
        type: 'range',
        isGlobal: true,
        maxLength: 2,
      },
      {
        name: 'read.devicePhotoCount',
        label: key('READ_METER_SITE_PHOTO_COUNT'),
        description: key('READ_METER_SITE_PHOTO_COUNT_DESCRIPTION'),
        type: 'range',
        isGlobal: true,
        maxLength: 2,
      },
      {
        name: 'check.meterPhotoCount',
        label: key('CHECK_SCALE_PHOTO_COUNT'),
        type: 'range',
        isGlobal: true,
        maxLength: 2,
      },
      {
        name: 'check.devicePhotoCount',
        label: key('CHECK_METER_SITE_PHOTO_COUNT'),
        type: 'range',
        isGlobal: true,
        maxLength: 2,
      },
      {
        name: 'check.documentPhotoCount',
        label: key('CHECK_DOCUMENT_PHOTO_COUNT'),
        type: 'range',
        isGlobal: true,
        maxLength: 2,
      },
      {
        name: 'healthAndSafety.photoCount',
        label: key('HEALTH_AND_SAFETY_PHOTO_COUNT'),
        type: 'range',
        isGlobal: true,
        maxLength: 2,
      },
    ],
  },
  {
    key: 'appointment',
    title: key('SECTION_APPOINTMENT'),
    rows: [
      {
        name: 'appointment.upcomingAppointmentsTimeframeInMinutes',
        label: key('UPCOMING_APPOINTMENTS_TIMEFRAME'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
      {
        name: 'appointment.upcomingAppointmentsCheckPeriodInMinutes',
        label: key('UPCOMING_APPOINTMENTS_CHECK_PERIOD'),
        type: 'number',
        isGlobal: true,
        maxLength: 4,
      },
    ],
  },
];

/**
 * The rows above build their labels at runtime, so a static scan of the source finds no reference
 * to those keys. Listing them here keeps the extractor from pruning them as unused.
 */
/* istanbul ignore next */
export const keepFieldDeviceSettingsKeysAlive = () => [
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.COLUMN_ELECTRICITY'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.COLUMN_GAS'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.SECTION_RETENTION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.TASK_DELETION_AFTER_READING'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.TASK_DELETION_AFTER_CLOSING'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_DELETION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.MESSAGE_DELETION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.DATA_SYNC_INTERVAL'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.DATA_SYNC_INTERVAL_DESCRIPTION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.SECTION_GPS'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.GPS_REQUIRED'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.GPS_VALIDITY'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.GPS_TRACKING'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.SECTION_DEVICE'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.READING_CONFIRMATION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.MAINTENANCE_MENU'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.SECTION_PHOTO'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_SCALE'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_METER_SITE'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_REPORT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_READING_FAILURE_CONSUMPTION_PLACE'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_READING_FAILURE_METER'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_READING_FAILURE_SCALE'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_DOCUMENT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_HEALTH_AND_SAFETY'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_HEALTH_AND_SAFETY_DESCRIPTION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_QUALITY_MINIMUM_SCORE'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_QUALITY_RETRY_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.WITHDRAWN_READING_TASK_DELETION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.WITHDRAWN_CHECK_TASK_DELETION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.CHECK_TASK_DELETION_AFTER_APPOINTMENT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.HEALTH_AND_SAFETY_DELETION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.HEALTH_AND_SAFETY_DELETION_DESCRIPTION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.SECTION_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.READ_SCALE_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.READ_METER_SITE_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.READ_METER_SITE_PHOTO_COUNT_DESCRIPTION'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.CHECK_SCALE_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.CHECK_METER_SITE_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.CHECK_DOCUMENT_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.HEALTH_AND_SAFETY_PHOTO_COUNT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.SECTION_APPOINTMENT'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.UPCOMING_APPOINTMENTS_TIMEFRAME'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.UPCOMING_APPOINTMENTS_CHECK_PERIOD'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_COUNT_MIN'),
  i18n.t('KOZOS_TEREPI_ESZKOZ_BEALLITASOK.PHOTO_COUNT_MAX'),
];
